import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Lock, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Eye, 
  EyeOff,
  Check,
  Shield
} from 'lucide-react';
import { API } from '../lib/api';

/**
 * SetPasswordForm Component
 * 
 * Used after email verification to set a password for the guest account.
 * This component can be used in:
 * - GuestOnboarding flow (after email verification)
 * - Standalone page for setting password
 */
export default function SetPasswordForm({ token, onComplete, onSkip }) {
  const { language } = useLanguage();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Password requirements
  const requirements = [
    { 
      met: password.length >= 8, 
      text: language === 'en' ? 'At least 8 characters' : 'Almeno 8 caratteri' 
    },
    { 
      met: /[a-zA-Z]/.test(password), 
      text: language === 'en' ? 'At least one letter' : 'Almeno una lettera' 
    },
    { 
      met: /\d/.test(password), 
      text: language === 'en' ? 'At least one number' : 'Almeno un numero' 
    },
    { 
      met: password && confirmPassword && password === confirmPassword, 
      text: language === 'en' ? 'Passwords match' : 'Le password corrispondono' 
    }
  ];
  
  const allRequirementsMet = requirements.every(r => r.met);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      setError(language === 'en' 
        ? 'Please meet all password requirements'
        : 'Soddisfa tutti i requisiti della password');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${API}/guest/set-password`,
        { password, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(true);
      toast.success(language === 'en' 
        ? 'Password set successfully!'
        : 'Password impostata con successo!');
      
      if (onComplete) {
        setTimeout(() => onComplete(), 1500);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || (language === 'en' 
        ? 'Error setting password'
        : 'Errore durante l\'impostazione della password'));
    } finally {
      setLoading(false);
    }
  };
  
  if (success) {
    return (
      <div className="text-center space-y-6 py-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        
        <div>
          <h2 className="text-xl font-cinzel font-semibold text-gray-900 mb-2">
            {language === 'en' ? 'Password Set!' : 'Password Impostata!'}
          </h2>
          <p className="text-gray-600 text-sm">
            {language === 'en' 
              ? 'Your account is now fully set up. You can login with your email and password.'
              : 'Il tuo account è ora completamente configurato. Puoi accedere con email e password.'}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Set Your Password' : 'Imposta la Tua Password'}
        </h2>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'Create a password for your permanent account.'
            : 'Crea una password per il tuo account permanente.'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="set-password" className="text-sm font-medium text-gray-700">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
            <Input
              id="set-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              placeholder="••••••••"
              className="pl-10 pr-10 h-11"
              required
              data-testid="set-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#C5A059]"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
            {language === 'en' ? 'Confirm Password' : 'Conferma Password'}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
            <Input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError(null);
              }}
              placeholder="••••••••"
              className="pl-10 pr-10 h-11"
              required
              data-testid="set-confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#C5A059]"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {/* Requirements checklist */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
          {requirements.map((req, i) => (
            <div 
              key={i} 
              className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-gray-500'}`}
            >
              {req.met ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-current" />
              )}
              <span>{req.text}</span>
            </div>
          ))}
        </div>
        
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        
        <Button
          type="submit"
          disabled={loading || !allRequirementsMet}
          className="w-full h-11 bg-[#1B4332] hover:bg-[#2D5A45] text-white font-medium"
          data-testid="submit-set-password-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            language === 'en' ? 'Set Password' : 'Imposta Password'
          )}
        </Button>
        
        {onSkip && (
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            className="w-full text-gray-500 hover:text-gray-700 text-sm"
          >
            {language === 'en' ? 'Skip for now' : 'Salta per ora'}
          </Button>
        )}
      </form>
      
      <p className="text-xs text-gray-500 text-center">
        {language === 'en'
          ? 'You can always set your password later from your account settings.'
          : 'Puoi sempre impostare la password in seguito dalle impostazioni del tuo account.'}
      </p>
    </div>
  );
}
